/**
 * Field names for a map layer, for tool configuration.
 *
 * Thin wrapper over the discovery each service already has: ArcGIS publishes
 * its schema at `?f=json`, GeoServer at WFS DescribeFeatureType, and a
 * file-backed layer carries its own features. Written here rather than inline
 * in the dialog so the tool editor stays a form and knows nothing about GIS
 * services.
 */

import type { AppModule, Layer } from '../types';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../utils/arcgis';
import { fetchWfsFields, fetchWfsFieldTypes, fetchWfsDistinctValues } from '../layers/sld';
import { parseLooseDate } from '../components/layers/attributeQuery';

/**
 * Field names on one layer. Never throws: a tool picker with an empty list is
 * usable ("leave empty for all fields"), a crashed dialog is not.
 */
export async function fetchLayerFieldNames(layer: Layer, module: AppModule): Promise<string[]> {
  try {
    // A file-backed layer carries its data, so the schema is right there.
    if (layer.localData) {
      const names = new Set<string>();
      for (const feature of layer.localData.features ?? []) {
        for (const key of Object.keys(feature.properties ?? {})) {
          if (!key.startsWith('_')) names.add(key);
        }
      }
      return Array.from(names);
    }

    if (layer.arcgisLayerId === undefined) {
      if (!module.geoServerUrl) return [];
      const fields = await fetchWfsFields(module.geoServerUrl, layer.name);
      return fields.map((field) => field.name);
    }

    const effectiveUrl = arcgisServiceUrlFor(layer, module);
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    if (!baseUrl) return [];

    const response = await fetch(withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}?f=json`, effectiveUrl));
    if (!response.ok) return [];
    const data = await response.json();
    if (data?.error) return [];

    return (data.fields ?? [])
      .map((field: any) => field?.name)
      .filter((name: unknown): name is string => typeof name === 'string');
  } catch (error) {
    console.warn(`[tools] field list failed for "${layer.name}"`, error);
    return [];
  }
}

/**
 * Which of a layer's fields hold numbers.
 *
 * Needed because a filter's quoting cannot be decided from the value someone
 * typed. Against a live service, both of these are errors:
 *
 *   ADDRNUM = 10      →  fails; ADDRNUM is a String column
 *   OBJECTID = '10'   →  fails; OBJECTID is numeric
 *
 * The value "10" looks identical in both cases, so guessing from it is wrong
 * half the time. The FIELD's type is the only thing that settles it.
 *
 * Never throws: an empty set means "quote everything", which is the safer
 * default — a quoted number is usually cast, an unquoted string never is.
 */
export async function fetchNumericFieldNames(
  layer: Layer,
  module: AppModule,
): Promise<Set<string>> {
  const numeric = new Set<string>();
  try {
    if (layer.localData) {
      for (const feature of layer.localData.features ?? []) {
        for (const [key, value] of Object.entries(feature.properties ?? {})) {
          if (typeof value === 'number') numeric.add(key);
        }
      }
      return numeric;
    }

    if (layer.arcgisLayerId === undefined) {
      // GeoServer's DescribeFeatureType is parsed for names only today, so
      // WMS layers fall back to quoting — CQL casts a quoted number.
      return numeric;
    }

    const effectiveUrl = arcgisServiceUrlFor(layer, module);
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    if (!baseUrl) return numeric;

    const response = await fetch(withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}?f=json`, effectiveUrl));
    if (!response.ok) return numeric;
    const data = await response.json();
    if (data?.error) return numeric;

    const NUMERIC_TYPES = [
      'esriFieldTypeOID',
      'esriFieldTypeInteger',
      'esriFieldTypeSmallInteger',
      'esriFieldTypeBigInteger',
      'esriFieldTypeDouble',
      'esriFieldTypeSingle',
    ];
    for (const field of data.fields ?? []) {
      if (typeof field?.name === 'string' && NUMERIC_TYPES.includes(field?.type)) {
        numeric.add(field.name);
      }
    }
    return numeric;
  } catch (error) {
    console.warn(`[tools] field types failed for "${layer.name}"`, error);
    return numeric;
  }
}

/**
 * Which of a layer's fields hold dates — the Time Slider tool's own field
 * picker. Same three-way branch and never-throws contract as
 * `fetchNumericFieldNames`, which this is modelled on directly.
 */
export async function fetchDateFieldNames(layer: Layer, module: AppModule): Promise<Set<string>> {
  const dates = new Set<string>();
  try {
    if (layer.localData) {
      // No schema to read, so a field counts as a date when MOST of its
      // sampled values parse as one — one stray blank or "n/a" should not
      // disqualify an otherwise-genuine date column.
      const seen = new Map<string, { total: number; dateLike: number }>();
      for (const feature of layer.localData.features ?? []) {
        for (const [key, value] of Object.entries(feature.properties ?? {})) {
          if (value === null || value === undefined || value === '') continue;
          const stats = seen.get(key) ?? { total: 0, dateLike: 0 };
          stats.total += 1;
          if (parseLooseDate(value) !== null) stats.dateLike += 1;
          seen.set(key, stats);
        }
      }
      for (const [key, stats] of seen) {
        if (stats.total > 0 && stats.dateLike / stats.total >= 0.8) dates.add(key);
      }
      return dates;
    }

    if (layer.arcgisLayerId === undefined) {
      if (!module.geoServerUrl) return dates;
      const fields = await fetchWfsFieldTypes(module.geoServerUrl, layer.name);
      for (const field of fields) {
        if (/date/i.test(field.type)) dates.add(field.name);
      }
      return dates;
    }

    const effectiveUrl = arcgisServiceUrlFor(layer, module);
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    if (!baseUrl) return dates;

    const response = await fetch(withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}?f=json`, effectiveUrl));
    if (!response.ok) return dates;
    const data = await response.json();
    if (data?.error) return dates;

    for (const field of data.fields ?? []) {
      if (typeof field?.name === 'string' && field?.type === 'esriFieldTypeDate') {
        dates.add(field.name);
      }
    }
    return dates;
  } catch (error) {
    console.warn(`[tools] date field list failed for "${layer.name}"`, error);
    return dates;
  }
}

/**
 * Every distinct CALENDAR DAY a date field actually takes, sorted ascending
 * — what the Time Slider's stops AND its date dropdown are both built from.
 *
 * Deliberately the real values the data has, not an evenly-spaced
 * interpolation across the overall min/max range: with sparse or unevenly
 * spaced dates, most interpolated stops would land between two real values
 * and change nothing on the map — which is what made dragging the slider,
 * or watching it auto-play, look like filtering had stopped working. Every
 * stop here corresponds to an actual change in what the map shows.
 */
export async function fetchDateValues(
  layer: Layer,
  module: AppModule,
  field: string,
): Promise<{ days: { day: string; ms: number }[] } | null> {
  /** Buckets raw values by calendar day (UTC), de-duplicated, sorted ascending. */
  const toDays = (raws: unknown[]): { day: string; ms: number }[] => {
    const seen = new Map<string, number>();
    for (const raw of raws) {
      const ms = parseLooseDate(raw);
      if (ms === null) continue;
      const day = new Date(ms).toISOString().slice(0, 10);
      if (!seen.has(day)) seen.set(day, Date.parse(`${day}T00:00:00.000Z`));
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([day, ms]) => ({ day, ms }));
  };

  try {
    if (layer.localData) {
      const raws = (layer.localData.features ?? []).map((feature) => feature.properties?.[field]);
      const days = toDays(raws);
      return days.length > 0 ? { days } : null;
    }

    if (layer.arcgisLayerId === undefined) {
      if (!module.geoServerUrl) return null;
      // Distinct RAW values (same WFS call the unique-value renderer uses) —
      // then bucketed to calendar days here, since a timestamped field would
      // otherwise list every distinct time of day as its own "date".
      const raws = await fetchWfsDistinctValues(module.geoServerUrl, layer.name, field, 1000);
      const days = toDays(raws);
      return days.length > 0 ? { days } : null;
    }

    const effectiveUrl = arcgisServiceUrlFor(layer, module);
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    if (!baseUrl) return null;

    const url =
      `${baseUrl}/${layer.arcgisLayerId}/query?f=json&where=1=1&returnGeometry=false` +
      `&outFields=${encodeURIComponent(field)}&returnDistinctValues=true&orderByFields=${encodeURIComponent(field)}`;
    const response = await fetch(withArcgisToken(url, effectiveUrl));
    if (!response.ok) return null;
    const data = await response.json();
    if (data?.error) return null;
    const raws = (data.features ?? []).map((feature: any) => feature?.attributes?.[field]);
    const days = toDays(raws);
    return days.length > 0 ? { days } : null;
  } catch (error) {
    console.warn(`[tools] date values failed for "${layer.name}" field="${field}"`, error);
    return null;
  }
}
